<template>
  <div class="child-details-container">
    <!-- Hero Profile Header & Sibling Switcher -->
    <ChildHeroHeader
      :child-info="childInfo"
      :child-id="childId"
      :is-dancer-route="isDancerRoute"
      :is-admin="isAdmin"
      :is-teacher="isTeacher"
      :is-parent="isParent"
      :can-edit="canEditProfile()"
      :is-editing-enabled="isEditingEnabled"
      :siblings-list="siblingsList"
      :logged-in-parent-email="loggedInParentEmail"
      :parent-email="getParentEmail()"
      :parent2-email="getParent2Email()"
      :balance="balance"
      :in-debt="inDebt"
      @enable-editing="enableEditing"
      @disable-editing="disableEditing"
      @open-delete-dancer="openDeleteDancerModal"
      @open-delete-request="openDeleteRequestModal"
      @child-added="onChildAdded"
    />

    <div v-if="childInfo" class="content-wrapper">
      <!-- Main Interactive Calendar & Events Section (Top for non-admins) -->
      <ChildCalendarEvents
        v-if="!isAdmin"
        :upcoming-lessons="upcomingLessons"
        :upcoming-competitions="upcomingCompetitions"
        :upcoming-galas="upcomingGalas"
        :calendar-events="calendarEvents"
      />

      <!-- Child Information Cards Grid -->
      <div class="info-grid">
        <!-- Diák adatai -->
        <ChildPersonalDetailsCard
          :child-info="childInfo"
          :is-admin="isAdmin"
          :is-teacher="isTeacher"
          :is-assistant="isAssistant"
          :can-edit="canEditProfile()"
          :is-editing-enabled="isEditingEnabled"
          @save-field="handleSaveChildField"
          @toggle-competitor="updateCompetitor"
          @open-forfeit-email="openForfeitEmailModal"
        />

        <!-- Csoport információ -->
        <ChildGroupsCard
          :child-info="childInfo"
          :child-groups="childGroups"
          :available-groups-for-user="availableGroupsForUser"
          :is-admin="isAdmin"
          :is-teacher="isTeacher"
          :is-assistant="isAssistant"
          @add-group="handleAddGroup"
          @remove-group="removeGroup"
          @set-primary-group="setPrimaryGroup"
        />

        <!-- Diák pénzügyek és adatok -->
        <ChildFinanceCard
          :child-id="childId"
          :is-admin="isAdmin"
          :is-teacher="isTeacher"
          :is-assistant="isAssistant"
          :balance="balance"
          :monthly-fee="monthlyFee"
          :monthly-fee-paid="monthlyFeePaid"
          :current-month="currentMonth"
          :fee-groups="feeGroups"
          :groups="groups"
          :fee-form="feeForm"
          :birth-year="birthYear"
          :birth-month="birthMonth"
          @save-birth="handleSaveBirth"
          @top-up-monthly="topUpMonthly"
          @open-custom-topup="openCustomTopUpModal"
          @add-fee-group="handleAddFeeGroup"
          @remove-fee-group="removeFeeGroup"
        />

        <!-- Gondviselő adatai -->
        <ChildGuardianCard
          :child-info="childInfo"
          :is-admin="isAdmin"
          :can-edit="canEditProfile()"
          :is-editing-enabled="isEditingEnabled"
          :is-parent="isParent"
          :is-dancer-route="isDancerRoute"
          :is-inviting-parent="isInvitingParent"
          :invite-message="inviteMessage"
          :invite-success="inviteSuccess"
          @save-parent-field="handleSaveParentField"
          @send-parent-invitation="sendParentInvitation"
          @open-message-parent-modal="openMessageParentModal"
          @open-forfeit-email="openForfeitEmailModal"
        />

        <!-- Házifeladat, Visszajelzés & Aktivítás -->
        <ChildNotesActivity
          :homework="homework"
          :child-info="childInfo"
          :is-admin="isAdmin"
          :is-teacher="isTeacher"
          :is-dancer-route="isDancerRoute"
          @get-hash="getHash"
          @update-active-status="handleUpdateActiveStatus"
        />
      </div>

      <!-- Main Interactive Calendar & Events Section (Bottom for admins) -->
      <ChildCalendarEvents
        v-if="isAdmin"
        :upcoming-lessons="upcomingLessons"
        :upcoming-competitions="upcomingCompetitions"
        :upcoming-galas="upcomingGalas"
        :calendar-events="calendarEvents"
      />
    </div>

    <!-- Modals -->
    <ChildParentMessageModal
      :show="showMessageParentModal"
      :parent-email="getParentEmail()"
      :is-sending="isSendingParentMessage"
      :feedback="parentMessageFeedback"
      :success="parentMessageSuccess"
      @close="closeMessageParentModal"
      @send="handleSendParentMessage"
    />

    <ChildTopUpModal
      :show="showCustomTopUpModal"
      :child-info="childInfo"
      :child-id="childId"
      :child-groups="childGroups"
      :balance="balance"
      :monthly-fee="monthlyFee"
      :is-saving="isTopUpSaving"
      @close="closeCustomTopUpModal"
      @submit="submitCustomTopUp"
    />

    <ChildDeleteModals
      :show-delete-dancer-modal="showDeleteDancerModal"
      :is-deleting-dancer="isDeletingDancer"
      :show-delete-request-modal="showDeleteRequestModal"
      :is-submitting-delete-request="isSubmittingDeleteRequest"
      :child-info="childInfo"
      @close-delete-dancer="closeDeleteDancerModal"
      @confirm-delete-dancer="confirmDeleteDancer"
      @close-delete-request="closeDeleteRequestModal"
      @submit-delete-request="handleSubmitDeleteRequest"
    />

    <ChildForfeitEmailModal
      :show="showForfeitEmailModal"
      :current-dancer-email="(childInfo && childInfo.dancer_email) ? childInfo.dancer_email : ''"
      :child-name="getChildFullName()"
      :is-submitting="isSubmittingForfeitEmail"
      :error="forfeitEmailError"
      @close="closeForfeitEmailModal"
      @confirm="handleForfeitEmail"
    />
  </div>
</template>

<script>
import axios from 'axios';
import toastMixin from '../mixins/toast.js';
import ChildHeroHeader from './child/ChildHeroHeader.vue';
import ChildCalendarEvents from './child/ChildCalendarEvents.vue';
import ChildPersonalDetailsCard from './child/ChildPersonalDetailsCard.vue';
import ChildGroupsCard from './child/ChildGroupsCard.vue';
import ChildFinanceCard from './child/ChildFinanceCard.vue';
import ChildGuardianCard from './child/ChildGuardianCard.vue';
import ChildNotesActivity from './child/ChildNotesActivity.vue';
import ChildParentMessageModal from './child/ChildParentMessageModal.vue';
import ChildTopUpModal from './child/ChildTopUpModal.vue';
import ChildDeleteModals from './child/ChildDeleteModals.vue';
import ChildForfeitEmailModal from './child/ChildForfeitEmailModal.vue';

export default {
  name: 'ChildItem',
  mixins: [toastMixin],
  components: {
    ChildHeroHeader,
    ChildCalendarEvents,
    ChildPersonalDetailsCard,
    ChildGroupsCard,
    ChildFinanceCard,
    ChildGuardianCard,
    ChildNotesActivity,
    ChildParentMessageModal,
    ChildTopUpModal,
    ChildDeleteModals,
    ChildForfeitEmailModal
  },
  data() {
    return {
      childInfo: null,
      childGroups: [],
      upcomingLessons: [],
      upcomingCompetitions: [],
      upcomingGalas: [],
      calendarEvents: [],
      isAdmin: false,
      isTeacher: false,
      isAssistant: false,
      userId: null,
      showDeleteDancerModal: false,
      isDeletingDancer: false,
      showDeleteRequestModal: false,
      deleteReason: '',
      isSubmittingDeleteRequest: false,
      isDancerRoute: false,
      eventGroups: [],
      homework: '',
      isEditingField: {
        childxskill: false,
        childxaddr: false,
        childxfamily_name: false,
        childxsure_name: false,
        parent1xfamily_name: false,
        parent1xsure_name: false,
        parent1xphone: false,
        parent1xaddr: false,
        parent2xfamily_name: false,
        parent2xsure_name: false,
        parent2xphone: false,
        parent2xaddr: false,
      },
      isEditingEnabled: false,
      isAddingGroup: false,
      selectedGroupToAdd: '',
      isPrimaryForNewGroup: false,
      birthYear: null,
      birthMonth: null,
      balance: 0,
      inDebt: false,
      monthlyFee: 0,
      monthlyFeePaid: false,
      currentMonth: '',
      showCustomTopUpModal: false,
      customTopUpForm: { amount: null, method: 'készpénz', note: '', group_id: 0 },
      isTopUpSaving: false,
      feeGroups: [],
      groups: [],
      feeForm: { billing_mode: 'monthly', group_id: 0 },
      parentInviteEmail: '',
      isInvitingParent: false,
      inviteMessage: '',
      inviteSuccess: false,
      siblingsList: [],
      isParent: false,
      showMessageParentModal: false,
      parentMessageSubject: '',
      parentMessageBody: '',
      isSendingParentMessage: false,
      parentMessageFeedback: '',
      parentMessageSuccess: false,
      loggedInParentEmail: '',
      showForfeitEmailModal: false,
      isSubmittingForfeitEmail: false,
      forfeitEmailError: '',
    };
  },
  computed: {
    availableGroupsForUser() {
      if (this.isAdmin) {
        return this.eventGroups;
      }
      return this.eventGroups;
    },
  },
  watch: {
    '$route.params.id': {
      handler(newId) {
        if (newId !== undefined) {
          this.childId = newId;
          this.isDancerRoute = this.$route.path.startsWith('/dancer/');
          if (this.$getChildId) {
            const list = this.$getChildId();
            if (Array.isArray(list) && list.length > 0) {
              this.siblingsList = list;
            }
          }
          this.fetchChildInfo().then(() => {
            this.fetchChildGroups();
            this.getHomework();
            this.getGroups();
            this.fetchFinance();
            this.fetchUpcomingLessons();
            this.fetchCompetitions();
            this.fetchEventsAndGalas();
          });
        }
      }
    }
  },
  mounted() {
    this.isAdmin = this.$isAdmin();
    this.isTeacher = this.$isTeacher();
    this.isAssistant = this.$isAssistant();
    this.isParent = this.$isParent ? this.$isParent() : false;
    this.userId = this.$getUserId();
    this.childId = this.$route.params.id;

    if (this.childId === undefined) {
      this.childId = this.$getChildId();
    }

    if (this.$getChildId) {
      const list = this.$getChildId();
      if (Array.isArray(list) && list.length > 0) {
        this.siblingsList = list;
      } else {
        try {
          const cached = JSON.parse(localStorage.getItem('childId'));
          if (Array.isArray(cached)) {
            this.siblingsList = cached;
          }
        } catch (e) {}
      }
    }

    this.isDancerRoute = this.$route.path.startsWith('/dancer/');
    this.fetchChildInfo().then(() => {
      this.fetchChildGroups();
      this.getHomework();
      this.getGroups();
      this.fetchFinance();
      this.fetchUpcomingLessons();
      this.fetchCompetitions();
      this.fetchEventsAndGalas();
    });
  },
  methods: {
    getChildFullName() {
      if (!this.childInfo) return '';
      const fam = this.childInfo.childxfamily_name || '';
      const sure = this.childInfo.childxsure_name || '';
      return `${fam} ${sure}`.trim();
    },
    openForfeitEmailModal() {
      this.forfeitEmailError = '';
      this.showForfeitEmailModal = true;
    },
    closeForfeitEmailModal() {
      this.showForfeitEmailModal = false;
      this.forfeitEmailError = '';
    },
    async handleForfeitEmail({ newDancerEmail }) {
      this.isSubmittingForfeitEmail = true;
      this.forfeitEmailError = '';
      try {
        const payload = {
          child_id: parseInt(this.childId),
          new_dancer_email: newDancerEmail || ''
        };
        const response = await axios.post(`${this.$BACKEND_HOST}/api/child/forfeit-email`, payload);
        if (response.status === 200) {
          if (this.showToast) {
            this.showToast('E-mail cím sikeresen átadva a gondviselőnek!', 'success');
          }
          this.closeForfeitEmailModal();

          if (window.sessionManager) {
            await window.sessionManager.validateSession();
          } else if (this.$sessionManager) {
            await this.$sessionManager.validateSession();
          }

          if (this.isDancerRoute && (!this.$isDancer || !this.$isDancer())) {
            this.$router.push(`/children/${this.childId}`);
            return;
          }

          await this.fetchChildInfo();
        } else {
          this.forfeitEmailError = response.data?.message || 'Hiba történt a módosítás során.';
        }
      } catch (err) {
        console.error('Error forfeiting email:', err);
        this.forfeitEmailError = err.response?.data?.message || err.message || 'Hiba történt a módosítás során.';
      } finally {
        this.isSubmittingForfeitEmail = false;
      }
    },
    hasParent() {
      if (!this.childInfo) return false;
      if (typeof this.childInfo.has_parent === 'boolean') {
        return this.childInfo.has_parent;
      }
      return !!this.getParentEmail();
    },
    getParentEmail() {
      if (!this.childInfo) return '';
      if (this.childInfo.parent_email) return this.childInfo.parent_email;
      return this.getParent2Email();
    },
    getParentFamilyName() {
      if (!this.childInfo) return '';
      if (this.childInfo.parent_family_name) return this.childInfo.parent_family_name;
      return this.getParent2FamilyName();
    },
    getParentSureName() {
      if (!this.childInfo) return '';
      if (this.childInfo.parent_sure_name) return this.childInfo.parent_sure_name;
      return this.getParent2SureName();
    },
    getParentPhone() {
      if (!this.childInfo) return '';
      if (this.childInfo.parent_phone) return this.childInfo.parent_phone;
      return this.getParent2Phone();
    },
    getParentAddr() {
      if (!this.childInfo) return '';
      if (this.childInfo.parent_addr) return this.childInfo.parent_addr;
      return this.getParent2Addr();
    },
    openMessageParentModal() {
      this.parentMessageSubject = '';
      this.parentMessageBody = '';
      this.parentMessageFeedback = '';
      this.showMessageParentModal = true;
    },
    closeMessageParentModal() {
      this.showMessageParentModal = false;
      this.parentMessageFeedback = '';
    },
    handleSendParentMessage({ subject, body }) {
      this.parentMessageSubject = subject;
      this.parentMessageBody = body;
      this.sendMessageToParent();
    },
    async sendMessageToParent() {
      if (!this.parentMessageBody || !this.parentMessageBody.trim()) {
        this.parentMessageFeedback = 'Kérjük, írj üzenetet a küldés előtt!';
        this.parentMessageSuccess = false;
        return;
      }
      this.isSendingParentMessage = true;
      this.parentMessageFeedback = '';
      try {
        const payload = {
          child_id: parseInt(this.childId) || (this.childInfo && this.childInfo.child_id ? this.childInfo.child_id : 0),
          subject: this.parentMessageSubject,
          message: this.parentMessageBody
        };
        const res = await fetch(`${this.$BACKEND_HOST}/api/child/message_parent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Hiba történt az üzenet küldésekor.');
        }
        this.parentMessageFeedback = 'Az üzenet sikeresen elküldve a szülőnek!';
        this.parentMessageSuccess = true;
        setTimeout(() => {
          this.closeMessageParentModal();
        }, 1800);
      } catch (err) {
        this.parentMessageFeedback = err.message || 'Hiba történt az üzenet küldésekor.';
        this.parentMessageSuccess = false;
      } finally {
        this.isSendingParentMessage = false;
      }
    },
    openDeleteDancerModal() {
      this.showDeleteDancerModal = true;
    },
    closeDeleteDancerModal() {
      this.showDeleteDancerModal = false;
      this.isDeletingDancer = false;
    },
    async confirmDeleteDancer() {
      this.isDeletingDancer = true;
      try {
        const payload = {
          child_id: parseInt(this.childId)
        };
        const res = await axios.post(`${this.$BACKEND_HOST}/api/del-child`, payload);
        if (res.status === 200) {
          if (this.showToast) {
            this.showToast('Táncos sikeresen törölve!', 'success');
          }
          this.closeDeleteDancerModal();
          this.$router.push(this.isAdmin ? '/settings/children' : '/children');
        } else {
          if (this.showToast) {
            this.showToast('Hiba történt a törlés során.', 'error');
          }
        }
      } catch (err) {
        console.error('Error deleting dancer:', err);
        const errMsg = (err.response && err.response.data && err.response.data.message) ? err.response.data.message : 'Hiba történt a törlés során.';
        if (this.showToast) {
          this.showToast(errMsg, 'error');
        }
      } finally {
        this.isDeletingDancer = false;
      }
    },
    openDeleteRequestModal() {
      this.deleteReason = '';
      this.showDeleteRequestModal = true;
    },
    closeDeleteRequestModal() {
      this.showDeleteRequestModal = false;
      this.deleteReason = '';
      this.isSubmittingDeleteRequest = false;
    },
    handleSubmitDeleteRequest(reason) {
      this.deleteReason = reason;
      this.submitDeleteRequest();
    },
    async submitDeleteRequest() {
      this.isSubmittingDeleteRequest = true;
      try {
        const payload = {
          child_id: parseInt(this.childId),
          reason: (this.deleteReason || '').trim()
        };
        const res = await axios.post(`${this.$BACKEND_HOST}/api/del-child-req`, payload);
        if (res.status === 200) {
          if (this.showToast) {
            this.showToast('Törlési kérelem sikeresen elküldve az adminisztrátoroknak!', 'success');
          }
          this.closeDeleteRequestModal();
        } else {
          if (this.showToast) {
            this.showToast('Nem sikerült elküldeni a kérelmet.', 'error');
          }
        }
      } catch (err) {
        console.error('Error submitting delete request:', err);
        const errMsg = (err.response && err.response.data && err.response.data.message) ? err.response.data.message : 'Nem sikerült elküldeni a kérelmet.';
        if (this.showToast) {
          this.showToast(errMsg, 'error');
        }
      } finally {
        this.isSubmittingDeleteRequest = false;
      }
    },
    getParent2Email() {
      if (!this.childInfo || !this.childInfo.parent2Email) return '';
      if (typeof this.childInfo.parent2Email === 'object') {
        return this.childInfo.parent2Email.String || '';
      }
      return this.childInfo.parent2Email || '';
    },
    getParent2FamilyName() {
      if (!this.childInfo || !this.childInfo.parent2xfamily_name) return '';
      if (typeof this.childInfo.parent2xfamily_name === 'object') {
        return this.childInfo.parent2xfamily_name.String || '';
      }
      return this.childInfo.parent2xfamily_name || '';
    },
    getParent2SureName() {
      if (!this.childInfo || !this.childInfo.parent2xsure_name) return '';
      if (typeof this.childInfo.parent2xsure_name === 'object') {
        return this.childInfo.parent2xsure_name.String || '';
      }
      return this.childInfo.parent2xsure_name || '';
    },
    getParent2Phone() {
      if (!this.childInfo || !this.childInfo.parent2xphone) return '';
      if (typeof this.childInfo.parent2xphone === 'object') {
        return this.childInfo.parent2xphone.String || '';
      }
      return this.childInfo.parent2xphone || '';
    },
    getParent2Addr() {
      if (!this.childInfo || !this.childInfo.parent2xaddr) return '';
      if (typeof this.childInfo.parent2xaddr === 'object') {
        return this.childInfo.parent2xaddr.String || '';
      }
      return this.childInfo.parent2xaddr || '';
    },
    async onChildAdded() {
      if (window.sessionManager) {
        await window.sessionManager.validateSession();
      }
      this.fetchChildInfo();
    },
    enableEditing(){
      this.isEditingEnabled = true;
    },
    disableEditing() {
      this.isEditingEnabled = false;
    },
    async fetchChildInfo() {
      try {
        if (this.childId == -1) return;
        const base = this.isDancerRoute
          ? `${this.$BACKEND_HOST}/api/dancer/${this.childId}`
          : `${this.$BACKEND_HOST}/api/child/${this.childId}`;
        const response = await fetch(base);
        if (!response.ok) throw new Error('Failed to fetch data');
        const main_data = await response.json();
        if (main_data.message === 'done') {
          const data = main_data.data;
          if (this.isDancerRoute && main_data.child_id) {
            this.childId = main_data.child_id;
          }
          const p2MailStr = (typeof data.parent_2_email === 'object' && data.parent_2_email) ? (data.parent_2_email.String || '') : (data.parent_2_email || '');
          const p2FamStr = (typeof data.parent_2_family_name === 'object' && data.parent_2_family_name) ? (data.parent_2_family_name.String || '') : (data.parent_2_family_name || '');
          const p2SureStr = (typeof data.parent_2_sure_name === 'object' && data.parent_2_sure_name) ? (data.parent_2_sure_name.String || '') : (data.parent_2_sure_name || '');
          const p2PhoneStr = (typeof data.parent_2_phone === 'object' && data.parent_2_phone) ? (data.parent_2_phone.String || '') : (data.parent_2_phone || '');
          const p2AddrStr = (typeof data.parent_2_addr === 'object' && data.parent_2_addr) ? (data.parent_2_addr.String || '') : (data.parent_2_addr || '');

          this.childInfo = {
            childxskill: data.skill,
            childxfamily_name: data.child_family_name,
            childxsure_name: data.child_sure_name,
            childxaddr: data.child_addr,
            childGrpId: data.child_grp_id,
            childDesiredGrpId: data.child_desired_grp_id,
            eventGroupName: data.event_group_name,
            eventGroupSchedule: data.event_group_schedule,
            parent1Email: data.parent_1_email,
            parent1xaddr: data.parent_1_addr,
            parent1xfamily_name: data.parent_1_family_name,
            parent1xsure_name: data.parent_1_sure_name,
            parent1xphone: data.parent_1_phone,
            mainUserBirthDate: data.main_user_birth_date,
            parent2Email: p2MailStr,
            parent2xaddr: { String: p2AddrStr, Valid: true },
            parent2xfamily_name: { String: p2FamStr, Valid: true },
            parent2xsure_name: { String: p2SureStr, Valid: true },
            parent2xphone: { String: p2PhoneStr, Valid: true },
            status: data.child_status,
            is_competitor: data.is_competitor || false,
            dancer_email: data.dancer_email || '',
            has_parent: data.has_parent,
            parent_email: data.parent_email || '',
          };

          if (p2MailStr && !this.parentInviteEmail) {
            this.parentInviteEmail = p2MailStr;
          }
        }
      } catch (error) {
        console.error('Error fetching child information:', error);
      }
    },
    async updateCompetitor(newValue) {
      try {
        const targetId = parseInt(this.childId) || parseInt(this.$route.params.id);
        const response = await axios.put(`${this.$BACKEND_HOST}/api/children/${targetId}/competitor`, {
          is_competitor: newValue
        });
        if (response.status === 200) {
          this.childInfo.is_competitor = newValue;
          if (this.showToast) {
            this.showToast(`Versenyzői státusz ${newValue ? 'bekapcsolva' : 'kikapcsolva'}!`, 'success');
          }
        }
      } catch (error) {
        console.error('Error updating competitor status:', error);
        if (this.showToast) {
          this.showToast('Hiba történt a státusz módosításakor', 'error');
        }
        if (this.childInfo) {
          this.childInfo.is_competitor = !newValue;
        }
      }
    },
    async fetchFinance() {
      await Promise.all([this.fetchBirth(), this.fetchBalance(), this.fetchGroups(), this.fetchFeeGroups()]);
    },
    async fetchBirth() {
      try {
        const r = await fetch(this.$BACKEND_HOST + `/api/students/${this.childId}/birth`);
        const d = await r.json();
        this.birthYear = d.birth_year || null;
        this.birthMonth = d.birth_month || null;
      } catch (e) { console.error(e); }
    },
    handleSaveBirth({ year, month }) {
      this.birthYear = year;
      this.birthMonth = month;
      this.saveBirth();
    },
    async saveBirth() {
      try {
        await fetch(this.$BACKEND_HOST + '/api/students/birth', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ child_id: parseInt(this.childId), birth_year: this.birthYear || 0, birth_month: this.birthMonth || 0 }),
        });
      } catch (e) { console.error(e); }
    },
    async fetchBalance() {
      try {
        const r = await fetch(this.$BACKEND_HOST + `/api/students/${this.childId}/balance`);
        const d = await r.json();
        this.balance = d.balance ?? d.wallet?.balance ?? 0;
        this.inDebt = d.in_debt === true || this.balance < 0;
        this.monthlyFee = d.monthly_fee || 0;
        this.monthlyFeePaid = d.monthly_fee_paid === true;
        this.currentMonth = d.current_month || '';
      } catch (e) { console.error(e); }
    },
    topUpMonthly() {
      const amountToPay = this.monthlyFee > 0 ? this.monthlyFee : 5000;
      this.openCustomTopUpModal(amountToPay, 'Havi csoportdíj');
    },
    openCustomTopUpModal(presetAmount = null, note = '') {
      let currentGrpId = 0;
      if (this.childInfo && this.childInfo.childGrpId) {
        if (typeof this.childInfo.childGrpId === 'object' && this.childInfo.childGrpId.Valid) {
          currentGrpId = Number(this.childInfo.childGrpId.Int64);
        } else if (typeof this.childInfo.childGrpId === 'number') {
          currentGrpId = this.childInfo.childGrpId;
        }
      }
      this.customTopUpForm = { 
        amount: presetAmount || null, 
        method: 'készpénz', 
        note: note || '',
        group_id: currentGrpId
      };
      this.showCustomTopUpModal = true;
    },
    closeCustomTopUpModal() {
      this.showCustomTopUpModal = false;
      this.customTopUpForm = { amount: null, method: 'készpénz', note: '', group_id: 0 };
    },
    async submitCustomTopUp(formData) {
      if (formData) {
        this.customTopUpForm = { ...formData };
      }
      if (!this.customTopUpForm.amount || this.customTopUpForm.amount <= 0) return;
      this.isTopUpSaving = true;
      try {
        const payload = {
          child_id: parseInt(this.childId),
          amount: parseFloat(this.customTopUpForm.amount),
          method: this.customTopUpForm.method,
          note: this.customTopUpForm.note,
          group_id: (this.customTopUpForm.group_id !== undefined && this.customTopUpForm.group_id !== null) ? parseInt(this.customTopUpForm.group_id) : 0
        };
        const r = await fetch(this.$BACKEND_HOST + '/api/students/balance/topup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (r.ok) {
          const d = await r.json();
          this.balance = d.balance ?? 0;
          this.inDebt = d.in_debt === true || this.balance < 0;
          this.closeCustomTopUpModal();
          await this.fetchChildInfo();
          await this.fetchBalance();
        } else {
          const errData = await r.json().catch(() => ({}));
          this.showToast('Hiba a feltöltés során: ' + (errData.message || 'Ismeretlen hiba'), 'error');
        }
      } catch (e) {
        console.error('Error in custom topup:', e);
        this.showToast('Hiba a feltöltés során: ' + (e.message || e), 'error');
      } finally {
        this.isTopUpSaving = false;
      }
    },
    async fetchFeeGroups() {
      try {
        const r = await fetch(this.$BACKEND_HOST + `/api/billing/plans/${this.childId}`);
        const d = await r.json();
        this.feeGroups = (d.plans || []).map(p => ({
          id: p.id,
          group_name: p.group_name,
          billing_mode: p.billing_mode,
          group_id: p.group_id,
        }));
      } catch (e) { console.error(e); }
    },
    handleAddFeeGroup(formData) {
      if (formData) {
        this.feeForm = { ...formData };
      }
      this.addFeeGroup();
    },
    async addFeeGroup() {
      if (!this.feeForm.group_id) return;
      try {
        const r = await fetch(this.$BACKEND_HOST + '/api/billing/plans', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            child_id: parseInt(this.childId),
            group_id: this.feeForm.group_id,
            billing_mode: this.feeForm.billing_mode,
            is_active: true,
          }),
        });
        if (r.ok) {
          this.feeForm = { billing_mode: 'monthly', group_id: 0 };
          this.fetchFeeGroups();
        }
      } catch (e) { console.error(e); }
    },
    async removeFeeGroup(id) {
      try {
        await fetch(this.$BACKEND_HOST + `/api/billing/plans/${id}`, { method: 'DELETE' });
        this.fetchFeeGroups();
      } catch (e) { console.error(e); }
    },
    async fetchGroups() {
      try {
        const r = await fetch(this.$BACKEND_HOST + '/api/settings/groups/info');
        const d = await r.json();
        this.groups = d.groups || [];
      } catch (e) { console.error(e); }
    },
    async getHash(respType) {
      try {
        const response = await axios.post(this.$BACKEND_HOST + '/api/getchildhash', {
          child_id: parseInt(this.childId),
          child_grp: this.childInfo.childGrpId.Int64,
        });

        if (response.status === 200 && response.data.message == "done") {
          this.$router.push({path:`/${respType}/${response.data.hash}`});
        }
      } catch (error) {
        console.error('Error updating event group:', error);
      }
    },
    handleUpdateActiveStatus(status) {
      if (this.childInfo && this.childInfo.status) {
        this.childInfo.status.String = status;
      }
      this.updateChildActiveStatus();
    },
    async updateChildActiveStatus() {
      try {
        const response = await axios.post(this.$BACKEND_HOST + '/api/update-child-active', {
          child_id: parseInt(this.childId),
          child_status: this.childInfo.status.String,
        });

        if (response.status === 200) {
          await this.fetchChildInfo();
        }
      } catch (error) {
        console.error('Error updating event group:', error);
      }
    },
    async getHomework() {
      try {
        const response = await axios.post(this.$BACKEND_HOST + '/api/gethomework', {
          child_id: parseInt(this.childId),
        });

        if (response.status === 200) {
          this.homework = response.data.homework;
        }
      } catch (error) {
        console.error('Error getting homework:', error);
      }
    },
    async getGroups() {
      var resp = await this.getGroupDates();
      this.eventGroups = [];
      
      if (resp && resp.groups) {
        resp.groups.forEach(g => {
          this.eventGroups.push({id:g.id,name:g.name});
        });
      }

      if (this.isTeacher && !this.isAdmin && this.userId) {
        try {
          const teacherResponse = await fetch(`${this.$BACKEND_HOST}/api/settings/teachers/groups?teacher_id=${this.userId}`);
          if (teacherResponse.ok) {
            const teacherData = await teacherResponse.json();
            const assignedGroupIds = teacherData.groups.map(g => g.id);
            this.eventGroups = this.eventGroups.filter(group => assignedGroupIds.includes(group.id));
          }
        } catch (error) {
          console.error("Error fetching teacher groups:", error);
          this.eventGroups = [];
        }
      }
    },
    async getGroupDates() {
      try {
        const response = await fetch(this.$BACKEND_HOST + '/api/groupdates');
        return await response.json();
      } catch (err) {
        this.error = err.message;
      }
    },
    async fetchChildGroups() {
      try {
        if (this.childId == -1) return;
        const response = await fetch(`${this.$BACKEND_HOST}/api/child/${this.childId}/groups`);
        if (!response.ok) throw new Error('Failed to fetch child groups');
        const data = await response.json();
        if (data.message === 'done') {
          this.childGroups = data.groups || [];
        }
      } catch (error) {
        console.error('Error fetching child groups:', error);
        this.childGroups = [];
      }
    },
    handleAddGroup({ groupId, isPrimary }) {
      this.selectedGroupToAdd = groupId;
      this.isPrimaryForNewGroup = isPrimary;
      this.addGroup();
    },
    async addGroup() {
      try {
        const response = await axios.post(this.$BACKEND_HOST + '/api/child/groups/add', {
          child_id: parseInt(this.childId),
          group_id: parseInt(this.selectedGroupToAdd),
          is_primary: this.isPrimaryForNewGroup,
        });

        if (response.status === 200) {
          await this.fetchChildGroups();
          this.selectedGroupToAdd = '';
          this.isPrimaryForNewGroup = false;
        }
      } catch (error) {
        console.error('Error adding group:', error);
      }
    },
    async removeGroup(groupId) {
      try {
        const response = await axios.post(this.$BACKEND_HOST + '/api/child/groups/remove', {
          child_id: parseInt(this.childId),
          group_id: parseInt(groupId),
        });

        if (response.status === 200) {
          await this.fetchChildGroups();
        }
      } catch (error) {
        console.error('Error removing group:', error);
      }
    },
    async setPrimaryGroup(groupId) {
      try {
        const response = await axios.post(this.$BACKEND_HOST + '/api/child/groups/set-primary', {
          child_id: parseInt(this.childId),
          group_id: parseInt(groupId),
        });

        if (response.status === 200) {
          await this.fetchChildGroups();
        }
      } catch (error) {
        console.error('Error setting primary group:', error);
      }
    },
    canEditProfile() {
      if (this.isAdmin) return true;
      if (this.isDancerRoute) return true;
      if (this.$isDancer && this.$isDancer()) return true;
      const sessChildId = this.$getChildId ? this.$getChildId() : null;
      if (sessChildId && (sessChildId == this.childId || sessChildId == parseInt(this.childId))) return true;
      const sessUserId = this.$getUserId ? this.$getUserId() : null;
      if (sessUserId && this.childInfo) {
        if (sessUserId == this.childInfo.mainUserId || sessUserId == this.childInfo.parentId) {
          return true;
        }
      }
      return false;
    },
    handleSaveChildField({ field, value }) {
      if (this.childInfo && value !== undefined) {
        this.childInfo[field] = value;
      }
      this.updateFieldChild(field);
    },
    async updateFieldChild(fieldName) {
      this.isEditingField[fieldName] = false;
      try {
        const response = await axios.post(this.$BACKEND_HOST + '/api/updatechildinfo', {
          chld_id: parseInt(this.childId),
          info_to_change: fieldName.split("x")[1],
          new_info: this.childInfo[fieldName],
        });
        if (response.status === 200 && this.showToast) {
          this.showToast('Adatok sikeresen frissítve!', 'success');
        }
      } catch (error) {
        console.error('Error updating item:', error);
        if (this.showToast) {
          this.showToast('Sikertelen frissítés!', 'error');
        }
      }
    },
    handleSaveParentField({ field, email, value }) {
      if (this.childInfo) {
        if (this.childInfo[field] && typeof this.childInfo[field] === 'object') {
          this.childInfo[field].String = value;
        } else {
          this.childInfo[field] = value;
        }
      }
      this.updateFieldParent(field, email);
    },
    async updateFieldParent(fieldName, email) {
      this.isEditingField[fieldName] = false;
      const targetEmail = email || this.getParent2Email();
      if (!targetEmail) {
        if (this.showToast) {
          this.showToast('Kérjük először add meg a gondviselő e-mail címét!', 'warning');
        }
        return;
      }
      try {
        let val = '';
        const fieldVal = this.childInfo[fieldName];
        if (typeof fieldVal === 'string') {
          val = fieldVal;
        } else if (fieldVal && typeof fieldVal === 'object') {
          val = fieldVal.String || '';
        }

        const response = await axios.post(this.$BACKEND_HOST + '/api/updateparentinfo', {
          child_id: parseInt(this.childId),
          parent_email: targetEmail,
          info_to_change: fieldName.split("x")[1],
          new_info: val,
        });
        if (response.status === 200 && this.showToast) {
          this.showToast('Adatok sikeresen frissítve!', 'success');
          await this.fetchChildInfo();
        }
      } catch (error) {
        console.error('Error updating item:', error);
        if (this.showToast) {
          this.showToast('Sikertelen frissítés!', 'error');
        }
      }
    },
    async sendParentInvitation(emailToInvite) {
      const email = typeof emailToInvite === 'string' && emailToInvite.trim() ? emailToInvite.trim() : this.parentInviteEmail;
      if (!email) return;
      this.isInvitingParent = true;
      this.inviteMessage = '';
      try {
        const response = await axios.post(`${this.$BACKEND_HOST}/api/child/invite_parent`, {
          child_id: parseInt(this.childId),
          parent_email: email
        });
        this.inviteSuccess = true;
        this.inviteMessage = response.data.message || 'Meghívó e-mail sikeresen elküldve!';
        this.isEditingField['parent2email'] = false;
        await this.fetchChildInfo();
      } catch (error) {
        this.inviteSuccess = false;
        this.inviteMessage = error.response?.data?.message || 'Hiba történt a meghívó elküldésekor.';
      } finally {
        this.isInvitingParent = false;
      }
    },
    async fetchCompetitions() {
      try {
        const r = await fetch(this.$BACKEND_HOST + '/api/competitions/home');
        const d = await r.json();
        this.upcomingCompetitions = d.events || [];
      } catch (e) { console.error(e); }
    },
    async fetchEventsAndGalas() {
      try {
        const [gRes, eRes] = await Promise.all([
          fetch(this.$BACKEND_HOST + '/api/public/galas'),
          fetch(this.$BACKEND_HOST + '/api/calendar-events')
        ]);
        const gData = gRes.ok ? await gRes.json() : {};
        const eData = eRes.ok ? await eRes.json() : {};
        this.upcomingGalas = gData.galas || [];
        this.calendarEvents = eData.events || [];
      } catch (e) { console.error(e); }
    },
    async fetchUpcomingLessons() {
      try {
        const targetId = this.childId || (this.childInfo && this.childInfo.id);
        const url = (targetId && targetId != -1)
          ? `${this.$BACKEND_HOST}/api/child/${targetId}/upcoming-lessons`
          : `${this.$BACKEND_HOST}/api/user/upcoming-lessons`;
        const r = await fetch(url);
        const d = await r.json();
        this.upcomingLessons = d.lessons || [];
      } catch (e) { console.error(e); }
    }
  }
};
</script>

<style scoped>
.child-details-container {
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  background: var(--color-background);
  min-height: 100vh;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
}

@media (max-width: 768px) {
  .child-details-container {
    padding: 1rem;
  }
  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
